'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UserRole = 'owner' | 'admin' | 'member';

export interface OrgRecord {
  id: string;
  name: string;
  slug: string;
  plan: string | null;
  logo_url: string | null;
  owner_id: string | null;
  created_at: string | null;
  role: string;
}

export interface OrganizationSettings {
  id: string;
  organization_id: string;
  onboarding_completed: boolean | null;
  business_type: string | null;
  team_size: string | null;
  industry: string | null;
  primary_goals: string[] | null;
  ai_business_summary: string | null;
  enabled_modules: string[] | null;
  accent_color: string | null;
  custom_logo_url: string | null;
  sheets_import_url: string | null;
  webhook_secret: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  display_name: string;
  role: string;
  onboarding_complete: boolean;
  google_calendar_token?: string;
  timezone: string;
  default_view: string;
  notifications_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

const LS_ORG_KEY = 'not_active_org_id';

// ─── Context shape ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  currentUser: any;
  currentOrganization: OrgRecord | null;
  organizationSettings: OrganizationSettings | null;
  userRole: UserRole | null;
  allOrganizations: OrgRecord[];
  switchOrganization: (orgId: string) => Promise<void>;
  isLoading: boolean;

  // Legacy fields
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const [allOrganizations, setAllOrganizations] = useState<OrgRecord[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<OrgRecord | null>(null);
  const [organizationSettings, setOrganizationSettings] = useState<OrganizationSettings | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const router = useRouter();

  // Guards to prevent duplicate fetches
  const orgFetchedForUser = useRef<string | null>(null);
  const initDone = useRef(false);

  // ── Fetch legacy user profile ──────────────────────────────────────────────
  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(data as UserProfile);
      }
    } catch {
      // profile fetch failed silently
    }
  }, []);

  // ── Fetch org settings for a given org ────────────────────────────────────
  const fetchOrgSettings = useCallback(async (orgId: string): Promise<OrganizationSettings | null> => {
    try {
      const { data, error } = await supabase
        .from('organization_settings')
        .select('*')
        .eq('organization_id', orgId)
        .single();
      if (error) {
        console.log('[AuthContext] org settings fetch error:', error.message);
        return null;
      }
      return data as OrganizationSettings;
    } catch (err) {
      console.log('[AuthContext] org settings exception:', err);
      return null;
    }
  }, []);

  // ── Core: load orgs for a user and determine active org ───────────────────
  const loadOrganizations = useCallback(async (authUser: any) => {
    if (!authUser) {
      setAllOrganizations([]);
      setCurrentOrganization(null);
      setOrganizationSettings(null);
      setUserRole(null);
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches for the same user session
    if (orgFetchedForUser.current === authUser.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const { data: orgs, error } = await supabase.rpc('get_user_organizations');

      if (error) {
        console.log('[AuthContext] get_user_organizations error:', error.message);
        setIsLoading(false);
        return;
      }

      const orgList: OrgRecord[] = (orgs ?? []) as OrgRecord[];
      setAllOrganizations(orgList);

      if (orgList.length === 0) {
        setCurrentOrganization(null);
        setOrganizationSettings(null);
        setUserRole(null);
        orgFetchedForUser.current = authUser.id;
        setIsLoading(false);
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        // Only skip redirect if already on onboarding or select-workspace pages
        const alreadyOnSafePath = ['/onboarding', '/select-workspace', '/reset-password'].some(
          (p) => currentPath.startsWith(p)
        );
        if (!alreadyOnSafePath) {
          router.push('/onboarding/new');
        }
        return;
      }

      if (orgList.length > 1) {
        const storedOrgId = typeof window !== 'undefined'
          ? localStorage.getItem(LS_ORG_KEY)
          : null;

        const storedOrg = storedOrgId
          ? orgList.find((o) => o.id === storedOrgId) ?? null
          : null;

        if (storedOrg) {
          const settings = await fetchOrgSettings(storedOrg.id);
          setCurrentOrganization(storedOrg);
          setOrganizationSettings(settings);
          setUserRole(normaliseRole(storedOrg.role));
          orgFetchedForUser.current = authUser.id;
          setIsLoading(false);
          const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
          const authPaths = ['/login', '/signup', '/auth/'];
          if (authPaths.some((p) => currentPath.startsWith(p))) {
            // If onboarding not completed, send to AI onboarding flow
            if (!settings?.onboarding_completed && storedOrg.slug) {
              router.push(`/onboarding/${storedOrg.slug}`);
            } else {
              router.push('/app');
            }
          }
          return;
        }

        setCurrentOrganization(null);
        setOrganizationSettings(null);
        setUserRole(null);
        orgFetchedForUser.current = authUser.id;
        setIsLoading(false);
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        // Redirect to select-workspace unless already there
        if (!currentPath.startsWith('/select-workspace')) {
          router.push('/select-workspace');
        }
        return;
      }

      // Exactly 1 org → auto-select
      const singleOrg = orgList[0];
      if (typeof window !== 'undefined') {
        localStorage.setItem(LS_ORG_KEY, singleOrg.id);
      }
      const settings = await fetchOrgSettings(singleOrg.id);
      setCurrentOrganization(singleOrg);
      setOrganizationSettings(settings);
      setUserRole(normaliseRole(singleOrg.role));
      orgFetchedForUser.current = authUser.id;

      // Redirect to main portal if coming from auth pages
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const authPaths = ['/login', '/signup', '/auth/'];
      if (authPaths.some((p) => currentPath.startsWith(p))) {
        // If onboarding not completed, send to AI onboarding flow
        if (!settings?.onboarding_completed && singleOrg.slug) {
          router.push(`/onboarding/${singleOrg.slug}`);
        } else {
          router.push('/app');
        }
      }
    } catch (err) {
      console.log('[AuthContext] loadOrganizations exception:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchOrgSettings, router]);

  // ── switchOrganization ─────────────────────────────────────────────────────
  const switchOrganization = useCallback(async (orgId: string) => {
    const org = allOrganizations.find((o) => o.id === orgId);
    if (!org) return;
    if (typeof window !== 'undefined') {
      localStorage.setItem(LS_ORG_KEY, orgId);
    }
    const settings = await fetchOrgSettings(orgId);
    setCurrentOrganization(org);
    setOrganizationSettings(settings);
    setUserRole(normaliseRole(org.role));
  }, [allOrganizations, fetchOrgSettings]);

  // ── Auth state listener — runs ONCE on mount ───────────────────────────────
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    // Subscribe to auth state changes FIRST to avoid missing events
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // TOKEN_REFRESHED: update session/user state but do NOT re-fetch orgs
      if (event === 'TOKEN_REFRESHED') {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        return;
      }

      // SIGNED_IN / INITIAL_SESSION: only re-fetch orgs if the user ID actually changed
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && newSession?.user) {
        setSession(newSession);
        setUser(newSession.user);

        // On INITIAL_SESSION (page load), skip org loading if user is on an auth page.
        // The explicit signIn() call in AuthContext will handle routing after login.
        if (event === 'INITIAL_SESSION') {
          const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
          const authPages = ['/login', '/signup', '/auth/'];
          if (authPages.some((p) => currentPath.startsWith(p))) {
            // User has an active session but is on an auth page — let middleware or
            // the login page handle the redirect. Don't fire loadOrganizations here
            // to avoid the flash-then-redirect issue.
            setIsLoading(false);
            return;
          }
        }

        if (orgFetchedForUser.current !== newSession.user.id) {
          orgFetchedForUser.current = null;
          fetchProfile(newSession.user.id);
          loadOrganizations(newSession.user);
        }
        return;
      }

      // SIGNED_OUT: clear everything
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        setAllOrganizations([]);
        setCurrentOrganization(null);
        setOrganizationSettings(null);
        setUserRole(null);
        orgFetchedForUser.current = null;
        setIsLoading(false);
        return;
      }

      // Other events: update session state only
      setSession(newSession);
      setUser(newSession?.user ?? null);
    });

    // Fallback: if INITIAL_SESSION never fires (e.g. no session), stop loading
    const fallbackTimer = setTimeout(() => {
      if (orgFetchedForUser.current === null && isLoading) {
        setIsLoading(false);
      }
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallbackTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Legacy auth methods ────────────────────────────────────────────────────
  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          display_name: metadata?.displayName || metadata?.fullName || '',
          role: metadata?.role || 'viewer',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: 'https://notesonthis.com/auth/callback',
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) {
      await fetchProfile(data.user.id);
      orgFetchedForUser.current = null;
      await loadOrganizations(data.user);
    }
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setAllOrganizations([]);
    setCurrentOrganization(null);
    setOrganizationSettings(null);
    setUserRole(null);
    orgFetchedForUser.current = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(LS_ORG_KEY);
    }
  };

  // getCurrentUser reads from cached session state — no network call
  const getCurrentUser = async () => {
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as UserProfile;
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('Not authenticated');
    const { error } = await supabase
      .from('user_profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id);
    if (error) throw error;
    setProfile((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  // ── Context value ──────────────────────────────────────────────────────────
  const value: AuthContextValue = {
    currentUser: user,
    currentOrganization,
    organizationSettings,
    userRole,
    allOrganizations,
    switchOrganization,
    isLoading,

    user,
    session,
    profile,
    loading: isLoading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    updateProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normaliseRole(raw: string): UserRole {
  if (raw === 'owner') return 'owner';
  if (raw === 'admin') return 'admin';
  return 'member';
}
